import {
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/solid";
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  Avatar,
  Button,
  Chip,
  Input,
  SelectItem,
  Spinner,
  Tooltip,
} from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CountryFlag from "../../components/common/countryFlag";
import { fetchCountries, getManagers } from "../../services/api";
import { Profile } from "../../types/types";
import { getStatusColor } from "../../utils/users/fightersAndManagers";
import { getInitials } from "../../utils/utilMethods";
import AppSelect from "../../components/common/appSelect";
const ManagerList: React.FC<{ profileTabs: React.ReactNode }> = ({
  profileTabs,
}) => {
  const { t } = useTranslation("managers");
  const { t: tSidebar } = useTranslation("sidebar");
  const navigate = useNavigate();
  const pageSize = 10;
  const [managers, setManagers] = useState<Profile[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = React.useRef(0);
  const [totalPages, setTotalPages] = useState(1);
  const getFilterValue = (value: string) =>
    value === "ALL" ? undefined : value || undefined;
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocations(await fetchCountries());
      } catch {
        setError(t("error_countries"));
      }
    };
    loadLocations();
  }, [t]);
  useEffect(() => {
    const timer = setTimeout(() => {
      pageRef.current = 0;
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    let isCancelled = false;
    const loadManagers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getManagers({
          name: debouncedSearch.trim() || undefined,
          location: getFilterValue(selectedLocation),
          type: getFilterValue(selectedType),
          gender: getFilterValue(selectedGender),
          page,
          pageSize,
        });
        if (isCancelled) {
          return;
        }
        pageRef.current = 0;
        setManagers(response.data);
        setTotalPages(response.totalPages);
        setHasMore(response.data.length > 0 && 1 < response.totalPages);
      } catch {
        if (isCancelled) {
          return;
        }
        setError(t("error_managers"));
        setManagers([]);
        setTotalPages(1);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    loadManagers();
    return () => {
      isCancelled = true;
    };
  }, [
    debouncedSearch,
    selectedLocation,
    selectedType,
    selectedGender,
    page,
    t,
  ]);
  const loadMore = React.useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    try {
      const response = await getManagers({
        name: debouncedSearch.trim() || undefined,
        location: getFilterValue(selectedLocation),
        type: getFilterValue(selectedType),
        gender: getFilterValue(selectedGender),
        page: nextPage,
        pageSize,
      });
      if (response && response.data && response.data.length > 0) {
        pageRef.current = nextPage;
        setManagers((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const newManagers = response.data.filter((m: Profile) => !ids.has(m.id));
          return [...prev, ...newManagers];
        });
        setTotalPages(response.totalPages);
        setHasMore(response.data.length > 0 && nextPage + 1 < response.totalPages);
      } else {
        setHasMore(false);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [
    loading,
    loadingMore,
    hasMore,
    debouncedSearch,
    selectedLocation,
    selectedType,
    selectedGender,
  ]);

  const observerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const target = observerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadMore]);

  const updateFilter =
    (setFilter: (value: string) => void) => (value: string) => {
      setFilter(value);
      pageRef.current = 0;
      setPage(0);
    };
  const resetFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedLocation("");
    setSelectedType("");
    setSelectedGender("");
    pageRef.current = 0;
    setPage(0);
  };
  const managerTypeLabels: Record<string, string> = {
    INDIVIDUAL: t("individual"),
    AGENCY: t("agency"),
  };
  const renderManagerCard = (manager: Profile) => {
    const fullName =
      `${manager.firstName || ""} ${manager.lastName || ""}`.trim();
    const managerType = (manager.manager?.type ?? "").toUpperCase();
    const organizationName = manager.manager?.organizationName;
    const typeLabel = managerTypeLabels[managerType];
    const joinedDate = manager.createdAt
      ? new Date(manager.createdAt).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "";
    return (
      <div
        key={manager.id}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/profile?profileId=${manager.id}`)}
        onKeyDown={(event) =>
          event.key === "Enter" && navigate(`/profile?profileId=${manager.id}`)
        }
        className="flex h-96 w-full cursor-pointer flex-col overflow-hidden rounded-3xl bg-white p-1 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)] transition-shadow duration-200 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
      >
        <div className="min-h-0 flex-1">
          <div className="h-full overflow-hidden rounded-[20px] bg-gray-100">
            {manager.profileImage ? (
              <img
                src={manager.profileImage}
                alt={fullName}
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Avatar
                  name={getInitials(manager.firstName, manager.lastName)}
                  size="lg"
                  className="h-20 w-20 text-lg"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex h-32 flex-col justify-between px-4 pb-4 pt-1">
          <div className="flex items-center justify-between">
            {typeLabel ? (
              <span className="flex h-5 items-center rounded-xl bg-gray-200 px-2 text-xs font-medium leading-4 text-gray-800">
                {typeLabel}
              </span>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <CountryFlag countryName={manager.residenceCountry} size={20} />
              {manager.verified && (
                <CheckBadgeIcon className="size-6 shrink-0 text-blue-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 pr-6">
            <div className="truncate text-base font-bold leading-6 text-gray-900">
              {fullName || t("na")}
            </div>
            {organizationName && (
              <div className="truncate text-xs font-normal leading-4 text-gray-500">
                {organizationName}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            {joinedDate ? (
              <div className="flex min-w-0 items-center gap-1">
                <CalendarDaysIcon className="h-4 w-4 shrink-0 text-gray-900" />
                <span className="truncate text-sm font-normal leading-5 text-gray-900">
                  {joinedDate}
                </span>
              </div>
            ) : (
              <span />
            )}
            <Chip
              size="sm"
              variant="flat"
              color={getStatusColor(manager.profileStatus)}
            >
              {manager.profileStatus}
            </Chip>
          </div>
        </div>
      </div>
    );
  };
  const renderSelect = (
    label: string,
    value: string,
    options: { key: string; label: string }[],
    onChange: (value: string) => void,
    widthClassName: string,
  ) => (
    <AppSelect
      aria-label={label}
      placeholder={label}
      selectedKeys={value ? [value] : []}
      onSelectionChange={(keys) =>
        onChange((Array.from(keys)[0] as string) || "")
      }
      size="sm"
      radius="md"
      className={widthClassName}
      classNames={{
        trigger: "h-9 bg-white border border-gray-200 shadow-none",
        value: "text-sm font-normal leading-5 text-gray-600",
      }}
    >
      {options.map((option) => (
        <SelectItem key={option.key}>{option.label}</SelectItem>
      ))}
    </AppSelect>
  );
  const locationOptions = [
    { key: "ALL", label: t("all_locations") },
    ...locations.map((location) => ({ key: location, label: location })),
  ];
  const typeOptions = [
    { key: "ALL", label: t("all_types") },
    { key: "INDIVIDUAL", label: t("individual") },
    { key: "AGENCY", label: t("agency") },
  ];
  const genderOptions = [
    { key: "MALE", label: t("Male") },
    { key: "FEMALE", label: t("Female") },
    { key: "OTHER", label: t("Others") },
  ];
  const activeFilterCount = [
    selectedLocation,
    selectedType,
    selectedGender,
  ].filter((value) => value && value !== "ALL").length;
  const renderFilterSelects = (isInDrawer: boolean) => (
    <>
      {renderSelect(
        t("location_placeholder"),
        selectedLocation,
        locationOptions,
        updateFilter(setSelectedLocation),
        isInDrawer ? "w-full" : "w-64",
      )}
      {renderSelect(
        t("manager_type_placeholder"),
        selectedType,
        typeOptions,
        updateFilter(setSelectedType),
        isInDrawer ? "w-full" : "w-48",
      )}
      {renderSelect(
        t("gender_placeholder"),
        selectedGender,
        genderOptions,
        updateFilter(setSelectedGender),
        isInDrawer ? "w-full" : "w-36",
      )}
    </>
  );
  return (
    <div className="flex min-h-screen w-full flex-col bg-[#f8f9fb] font-['Plus_Jakarta_Sans']">
      <div className="hidden bg-white px-6 pt-6 pb-5 lg:block border-b border-gray-100">
        <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">
          Profile list
        </h1>
        <div className="border-b border-gray-200 my-4" />
        <div className="flex flex-wrap items-center gap-2.5">
          {profileTabs}
          <Input
            aria-label={t("search_name_placeholder")}
            placeholder={t("search_name_placeholder")}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            startContent={
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            }
            size="sm"
            radius="md"
            className="w-72"
            classNames={{
              inputWrapper: "h-9 bg-white border border-gray-200 shadow-none",
              input: "text-sm font-normal leading-5 placeholder:text-gray-400",
            }}
          />
          {renderFilterSelects(false)}
          <Tooltip content={t("reset_filters_tooltip")}>
            <Button
              isIconOnly
              variant="light"
              onPress={resetFilters}
              aria-label={t("reset_filters_tooltip")}
              className="h-9 w-9 min-w-9 rounded-xl border border-gray-200 bg-white text-gray-500"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2.5 bg-[#f8f9fb] px-4 pb-2 pt-4 lg:hidden">
        {isSearchOpen && (
          <div className="min-w-0 flex-1">
            <Input
              autoFocus
              aria-label={t("search_name_placeholder")}
              placeholder={t("search_name_placeholder")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              startContent={
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              }
              size="sm"
              radius="full"
              className="w-full"
              classNames={{
                inputWrapper:
                  "h-9 rounded-full border border-gray-200 bg-white shadow-none",
                input:
                  "text-sm font-normal leading-5 placeholder:text-gray-400",
              }}
            />
          </div>
        )}
        <button
          type="button"
          onClick={() => setIsSearchOpen((isOpen) => !isOpen)}
          aria-label={tSidebar("search")}
          className={`flex size-9 min-w-9 shrink-0 items-center justify-center rounded-2xl border shadow-sm transition-colors ${isSearchOpen ? "border-blue-200 bg-blue-50 text-blue-600" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setIsFilterDrawerOpen(true)}
          aria-label={tSidebar("filter")}
          className="relative flex size-9 min-w-9 shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          <FunnelIcon className="h-4 w-4" />
          {activeFilterCount > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
      {isFilterDrawerOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center lg:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px]"
            onClick={() => setIsFilterDrawerOpen(false)}
          />
          <div className="relative z-10 flex max-h-[85vh] w-full flex-col gap-4 overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">
                  {tSidebar("filters")}
                </h2>
                {activeFilterCount > 0 && (
                  <span className="flex size-5 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                aria-label={tSidebar("closeFilters")}
                className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col gap-3">
              {renderFilterSelects(true)}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex h-10 items-center gap-1.5 rounded-xl border border-gray-200 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {tSidebar("resetFilters")}
              </button>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="h-10 flex-1 rounded-xl bg-blue-600 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                {tSidebar("applyFilters")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 px-4 pb-6 pt-4 lg:px-6 lg:pt-6">
        {error && (
          <div className="mb-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1800px]:grid-cols-6">
            {Array.from({ length: pageSize }).map((_, index) => (
              <div
                key={index}
                className="flex h-96 animate-pulse flex-col justify-between rounded-3xl border border-slate-100 bg-white p-1 shadow-sm"
              >
                <div className="h-56 rounded-[20px] bg-slate-200/80" />
                <div className="flex h-32 flex-col justify-between px-4 pb-4 pt-2">
                  <div className="flex items-center justify-between">
                    <div className="h-4 w-16 rounded-lg bg-slate-200" />
                    <div className="flex items-center gap-1.5">
                      <div className="h-4 w-5 rounded bg-slate-200" />
                      <div className="h-4 w-4 rounded-full bg-slate-200" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-5 w-3/4 rounded-lg bg-slate-200" />
                    <div className="h-3 w-1/2 rounded bg-slate-100" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="h-4 w-20 rounded bg-slate-200" />
                    <div className="h-5 w-14 rounded-full bg-slate-200" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : managers.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1800px]:grid-cols-6">
            {managers.map(renderManagerCard)}
          </div>
        ) : (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-gray-200 bg-white">
            <MagnifyingGlassIcon className="h-8 w-8 text-gray-300" />
            <h3 className="mt-4 text-base font-semibold text-gray-900">
              {t("table_empty_content")}
            </h3>
          </div>
        )}
      </div>
        {managers.length > 0 && hasMore && (
          <div ref={observerRef} className="w-full flex items-center justify-center py-8">
            {loadingMore && <Spinner size="md" color="primary" />}
          </div>
        )}
    </div>
  );
};
export default ManagerList;
